import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { MapContainer, TileLayer, Marker, Polyline, useMapEvents, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import markerIcon from "leaflet/dist/images/marker-icon.png";
import markerIcon2x from "leaflet/dist/images/marker-icon-2x.png";
import markerShadow from "leaflet/dist/images/marker-shadow.png";
import api, { handleToolError, maybeShow3DayOffer } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Combobox } from "@/components/ui/combobox";
import { TrafficLight } from "@/components/TrafficLight";
import { OneAnalysisLeft } from "@/components/OneAnalysisLeft";
import { AreaChart, Area, XAxis, YAxis, Tooltip as RTooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { Loader2, MapPin, Info, Navigation, Search } from "lucide-react";
import { toast } from "sonner";

L.Marker.prototype.options.icon = L.icon({
  iconUrl: markerIcon, iconRetinaUrl: markerIcon2x, shadowUrl: markerShadow,
  iconSize: [25, 41], iconAnchor: [12, 41], popupAnchor: [1, -34], shadowSize: [41, 41],
});

function ClickHandler({ onClick }) {
  useMapEvents({ click(e) { onClick(e.latlng); } });
  return null;
}

function FlyTo({ target }) {
  const map = useMap();
  useEffect(() => { if (target) map.setView([target.lat, target.lng], 14); }, [target, map]);
  return null;
}

export function RouteMap() {
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const [vehicles, setVehicles] = useState([]);
  const [vehicleId, setVehicleId] = useState("");
  const [origin, setOrigin] = useState(null);
  const [dest, setDest] = useState(null);
  const [save, setSave] = useState(false);
  const [result, setResult] = useState(null);
  const [credit, setCredit] = useState(null);
  const [loading, setLoading] = useState(false);
  const [originQuery, setOriginQuery] = useState("");
  const [destQuery, setDestQuery] = useState("");
  const [searching, setSearching] = useState(null);
  const [flyTarget, setFlyTarget] = useState(null);

  const geocode = async (q) => {
    const { data } = await api.get("/route/geocode", { params: { q } });
    if (!data.results?.length) return null;
    return data.results[0];
  };

  const doSearch = async (which) => {
    const q = which === "origin" ? originQuery : destQuery;
    if (!q.trim()) return;
    setSearching(which);
    try {
      const pos = await geocode(q);
      if (!pos) { toast.error("Lokasi tidak ditemukan. Coba kata kunci lain."); return; }
      if (which === "origin") setOrigin(pos); else setDest(pos);
      setFlyTarget({ ...pos });
      setResult(null);
    } catch { toast.error("Gagal mencari lokasi."); }
    finally { setSearching(null); }
  };

  useEffect(() => {
    Promise.all([
      api.get("/vehicles", { params: { category: "motor_listrik" } }),
      api.get("/vehicles", { params: { category: "sepeda_listrik" } }),
    ]).then(([m, b]) => setVehicles([...m.data.items, ...b.data.items])).catch(() => {});
  }, []);

  const onMapClick = (latlng) => {
    setResult(null);
    if (!origin) { setOrigin(latlng); }
    else if (!dest) { setDest(latlng); }
    else { setOrigin(latlng); setDest(null); }
  };

  const analyze = async () => {
    if (!vehicleId) { toast.error("Pilih kendaraan."); return; }
    if (!origin || !dest) { toast.error("Tandai titik awal dan tujuan di peta."); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/analysis/route", {
        origin: { lat: origin.lat, lng: origin.lng }, dest: { lat: dest.lat, lng: dest.lng },
        vehicle_id: vehicleId, save,
      });
      setResult(data);
      maybeShow3DayOffer(data.credit);
      setCredit(data.credit);
      await checkAuth();
      toast.success("Analisis rute berhasil dibuat!");
    } catch (err) { handleToolError(err, navigate); }
    finally { setLoading(false); }
  };

  const reset = () => { setOrigin(null); setDest(null); setResult(null); };

  return (
    <div className="grid lg:grid-cols-2 gap-6" data-testid="route-map">
      <div className="space-y-4">
        <Card className="rounded-2xl p-5 space-y-4">
          <div>
            <Label>Kendaraan Listrik</Label>
            <div className="mt-1">
              <Combobox
                testId="route-vehicle-select"
                options={vehicles.map((v) => ({ value: v.vehicle_id, label: `${v.brand} ${v.model}` }))}
                value={vehicleId}
                onChange={setVehicleId}
                placeholder="Pilih kendaraan"
                searchPlaceholder="Ketik merek atau model…"
                emptyText="Kendaraan tidak ditemukan."
              />
            </div>
          </div>
          <div className="space-y-2">
            <div className="flex gap-2">
              <Input data-testid="route-origin-search" value={originQuery} onChange={(e) => setOriginQuery(e.target.value)} onKeyDown={(e) => e.key === "Enter" && doSearch("origin")} placeholder="Cari titik awal (mis. Monas, Jakarta)" className="rounded-xl h-10" />
              <Button data-testid="route-origin-search-btn" onClick={() => doSearch("origin")} disabled={searching === "origin"} variant="outline" className="rounded-xl px-3">{searching === "origin" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}</Button>
            </div>
            <div className="flex gap-2">
              <Input data-testid="route-dest-search" value={destQuery} onChange={(e) => setDestQuery(e.target.value)} onKeyDown={(e) => e.key === "Enter" && doSearch("dest")} placeholder="Cari titik tujuan (mis. Puncak, Bogor)" className="rounded-xl h-10" />
              <Button data-testid="route-dest-search-btn" onClick={() => doSearch("dest")} disabled={searching === "dest"} variant="outline" className="rounded-xl px-3">{searching === "dest" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}</Button>
            </div>
          </div>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <MapPin className="w-4 h-4 text-teal" />
            {!origin ? "Klik peta untuk titik AWAL" : !dest ? "Klik peta untuk titik TUJUAN" : "Titik siap. Tekan Analisis Rute."}
          </div>
          <label className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer">
            <Checkbox data-testid="route-save" checked={save} onCheckedChange={setSave} />
            Simpan rute ini (jika tidak dicentang, detail rute tidak disimpan permanen)
          </label>
          <div className="flex gap-2">
            <Button data-testid="route-analyze" onClick={analyze} disabled={loading} className="rounded-full bg-teal hover:bg-teal-hover flex-1">
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Navigation className="w-4 h-4 mr-2" />Analisis Rute</>}
            </Button>
            <Button data-testid="route-reset" onClick={reset} variant="outline" className="rounded-full">Ulang</Button>
          </div>
          <p className="text-xs text-muted-foreground">Sumber rute & elevasi: OpenStreetMap / OSRM + Open-Elevation. Hasil estimasi berdasarkan sampel titik rute.</p>
        </Card>

        <div className="rounded-2xl overflow-hidden border border-border" style={{ height: 360 }}>
          <MapContainer center={[-6.2, 106.816]} zoom={12} style={{ height: "100%", width: "100%" }}>
            <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution="&copy; OpenStreetMap" />
            <ClickHandler onClick={onMapClick} />
            <FlyTo target={flyTarget} />
            {origin && <Marker position={[origin.lat, origin.lng]} />}
            {dest && <Marker position={[dest.lat, dest.lng]} />}
            {result?.geometry && <Polyline positions={result.geometry} color="#00BFA5" weight={5} />}
          </MapContainer>
        </div>
      </div>

      <div>
        {result ? (
          <Card className="rounded-2xl p-6" data-testid="route-result">
            <TrafficLight status={result.result.status} size="lg" className="mb-4" />
            <p className="font-heading font-bold text-lg text-navy mb-4">{result.result.verdict}</p>
            <div className="grid grid-cols-3 gap-3 text-sm mb-4">
              <Stat l="Tanjakan tercuram" v={`${result.max_grade}%`} />
              <Stat l="Jarak rute" v={`${result.distance_km} km`} />
              <Stat l="Total naik" v={`${result.elevation_gain_m} m`} />
            </div>
            {result.profile?.length > 0 && (
              <div className="mb-4">
                <p className="font-semibold text-navy text-sm mb-2">Profil Elevasi</p>
                <ResponsiveContainer width="100%" height={160}>
                  <AreaChart data={result.profile}>
                    <defs><linearGradient id="el" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#00BFA5" stopOpacity={0.5} /><stop offset="100%" stopColor="#00BFA5" stopOpacity={0} /></linearGradient></defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis dataKey="dist_km" fontSize={11} tickFormatter={(v) => `${v}km`} />
                    <YAxis fontSize={11} width={38} tickFormatter={(v) => `${v}m`} />
                    <RTooltip formatter={(v) => `${v} m`} labelFormatter={(v) => `${v} km`} />
                    <Area type="monotone" dataKey="elevation" stroke="#00BFA5" strokeWidth={2} fill="url(#el)" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            )}
            {credit?.remaining === 1 && <div className="mb-4"><OneAnalysisLeft to="/hitung" label="Lanjutkan Analisis" /></div>}
            <div className="bg-slate-50 rounded-xl p-4 flex gap-3">
              <Info className="w-5 h-5 text-teal flex-shrink-0 mt-0.5" />
              <p className="text-xs text-muted-foreground">
                Analisis memakai tanjakan tercuram pada rute sebagai kondisi terberat. Sumber: {result.meta.route_source}. Data kendaraan: {result.meta.source?.source_name} (keyakinan {result.result.confidence}). {result.meta.saved ? "Rute disimpan." : "Detail rute tidak disimpan permanen."} Hasil estimasi, bukan jaminan.
              </p>
            </div>
          </Card>
        ) : (
          <Card className="rounded-2xl p-8 text-center text-muted-foreground h-full flex flex-col items-center justify-center" data-testid="route-empty">
            <Navigation className="w-12 h-12 mb-3 opacity-40" />
            Tandai titik awal & tujuan di peta, pilih kendaraan, lalu tekan Analisis Rute.
          </Card>
        )}
      </div>
    </div>
  );
}

function Stat({ l, v }) {
  return <div className="bg-secondary rounded-xl p-3"><p className="text-xs text-muted-foreground">{l}</p><p className="font-bold text-navy">{v}</p></div>;
}
